(() => {
  if (
    !window.customElements ||
    !window.fetch ||
    !window.AbortController ||
    !window.URL ||
    !window.URLSearchParams ||
    !window.DOMParser ||
    !window.MutationObserver ||
    !window.CustomEvent ||
    !window.Set ||
    !Array.from ||
    !Element.prototype.closest ||
    !Element.prototype.matches ||
    !URLSearchParams.prototype.sort
  ) {
    return;
  }

  if (customElements.get('mm-collection-scroll')) return;

  class CollectionScroll extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.container = this.closest('#ProductGridContainer');
      this.section =
        this.closest('.shopify-section') ||
        (this.container && this.container.parentElement);

      this.grid =
        this.container && this.container.querySelector('#product-grid');

      this.button = this.querySelector('[data-scroll-load]');
      this.controls = this.querySelector('[data-scroll-controls]');
      this.status = this.querySelector('[data-scroll-status]');
      this.pagination = this.querySelector('[data-scroll-pagination]');

      if (
        !this.section ||
        !this.grid ||
        !this.button ||
        !this.controls ||
        !this.status ||
        !this.pagination
      ) {
        return;
      }

      this.listeners = [];

      try {
        this.next = this.dataset.nextUrl || '';
        this.page = Number(this.dataset.page);
        this.context = this.contextKey();
        this.state = this.next ? 'idle' : 'done';

        this.listen(this.button, 'click', () => {
          if (this.state === 'filter-error') {
            window.location.reload();
          } else {
            this.load();
          }
        });

        this.listen(
          this.section,
          'input',
          (event) => this.onFilterInput(event),
          true
        );

        this.listen(
          this.section,
          'change',
          (event) => this.onFilterInput(event),
          true
        );

        this.listen(
          this.section,
          'click',
          (event) => {
            const target = event.target;

            if (
              target instanceof Element &&
              target.closest('facet-remove')
            ) {
              this.pause();
            }
          },
          true
        );

        this.listen(window, 'popstate', () => {
          if (this.contextKey() !== this.context) this.pause();
        });

        const collection = this.container.querySelector('.collection');

        if (collection) {
          this.filterObserver = new MutationObserver(() => {
            if (collection.classList.contains('loading')) this.pause();
          });

          this.filterObserver.observe(collection, {
            attributes: true,
            attributeFilter: ['class']
          });
        }

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              if (
                entries.some((entry) => entry.isIntersecting) &&
                this.state === 'idle'
              ) {
                this.load();
              }
            },
            { rootMargin: '0px 0px 500px 0px' }
          );
        }

        this.initialized = true;
        this.render();
        this.arm();
      } catch (error) {
        this.disconnect();
      }
    }

    listen(target, type, handler, capture) {
      const useCapture = Boolean(capture);

      target.addEventListener(type, handler, useCapture);

      this.listeners.push({
        target,
        type,
        handler,
        capture: useCapture
      });
    }

    disconnectedCallback() {
      this.disconnect();
    }

    disconnect() {
      (this.listeners || []).forEach((listener) => {
        listener.target.removeEventListener(
          listener.type,
          listener.handler,
          listener.capture
        );
      });

      this.listeners = [];

      if (this.request) this.request.abort();
      this.request = null;

      clearTimeout(this.timeout);
      clearTimeout(this.filterTimeout);
      clearTimeout(this.frame);

      if (this.observer) this.observer.disconnect();
      if (this.filterObserver) this.filterObserver.disconnect();
      if (this.revealObserver) this.revealObserver.disconnect();

      this.observer = null;
      this.filterObserver = null;
      this.revealObserver = null;

      if (this.grid) this.grid.removeAttribute('aria-busy');
      if (this.section) this.section.classList.remove('mm-scroll-pending');
      if (this.pagination) this.pagination.hidden = false;
      if (this.controls) this.controls.hidden = true;
      if (this.status) this.status.textContent = '';

      this.initialized = false;
    }

    contextKey() {
      const url = new URL(window.location.href);

      url.searchParams.delete('page');
      url.searchParams.delete('section_id');
      url.searchParams.sort();

      return url.pathname + '?' + url.searchParams.toString();
    }

    onFilterInput(event) {
      const target = event.target;

      if (
        target instanceof Element &&
        target.closest('facet-filters-form') &&
        target.matches('[name]')
      ) {
        this.pause();
      }
    }

    pause() {
      clearTimeout(this.filterTimeout);

      this.state = 'paused';

      if (this.request) this.request.abort();
      this.request = null;

      clearTimeout(this.timeout);
      clearTimeout(this.frame);

      if (this.observer) this.observer.disconnect();

      this.grid.removeAttribute('aria-busy');
      this.render();

      this.filterTimeout = setTimeout(() => {
        if (!this.isConnected || this.state !== 'paused') return;

        this.state = 'filter-error';
        this.render();
      }, 15000);
    }

    render() {
      const loading = this.state === 'loading';
      const paused = this.state === 'paused';
      const done = this.state === 'done';
      const error = this.state === 'error';
      const filterError = this.state === 'filter-error';

      this.controls.hidden = done || paused;
      this.button.disabled = loading;

      this.button.textContent = filterError
        ? this.dataset.reloadText
        : loading
          ? this.dataset.loadingText
          : error
            ? this.dataset.retryText
            : this.dataset.loadText;

      this.status.textContent = filterError
        ? this.dataset.filterErrorText
        : error
          ? this.dataset.errorText
          : '';

      this.pagination.hidden = !error;

      this.section.classList.toggle(
        'mm-scroll-pending',
        !done && !error && !filterError
      );
    }

    arm() {
      if (!this.observer || this.state !== 'idle' || !this.isConnected) return;

      this.observer.unobserve(this.controls);
      this.observer.observe(this.controls);
    }

    prepareAnimations(cards) {
      const nodes = [];

      cards.forEach((card) => {
        if (card.classList.contains('scroll-trigger')) {
          nodes.push(card);
        }

        Array.from(card.querySelectorAll('.scroll-trigger')).forEach(
          (node) => nodes.push(node)
        );
      });

      if (!nodes.length) return;

      if (
        !('IntersectionObserver' in window) ||
        (
          window.matchMedia &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches
        )
      ) {
        nodes.forEach((node) => {
          node.classList.remove('scroll-trigger--offscreen');
          node.classList.add('scroll-trigger--cancel');
        });

        return;
      }

      if (!this.revealObserver) {
        this.revealObserver = new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry, index) => {
              if (!entry.isIntersecting) return;

              entry.target.classList.remove('scroll-trigger--offscreen');

              if (entry.target.hasAttribute('data-cascade')) {
                entry.target.style.setProperty('--animation-order', index);
              }

              observer.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -50px 0px' }
        );
      }

      nodes.forEach((node) => {
        node.classList.remove('scroll-trigger--cancel');
        node.classList.add('scroll-trigger--offscreen');
        this.revealObserver.observe(node);
      });
    }

    async load() {
      if (
        !this.isConnected ||
        !this.next ||
        (this.state !== 'idle' && this.state !== 'error')
      ) {
        return;
      }

      if (
        this.contextKey() !== this.context ||
        this.container.querySelector('.collection.loading')
      ) {
        this.pause();
        return;
      }

      const request = new AbortController();

      this.request = request;
      this.state = 'loading';

      if (this.observer) {
        this.observer.unobserve(this.controls);
      }

      this.grid.setAttribute('aria-busy', 'true');
      this.render();

      this.timeout = setTimeout(() => request.abort(), 15000);

      try {
        const url = new URL(this.next, window.location.href);

        if (url.origin !== window.location.origin) {
          throw new Error('Invalid next-page origin');
        }

        url.searchParams.set('section_id', this.dataset.sectionId);

        const response = await fetch(url.href, {
          signal: request.signal,
          credentials: 'same-origin'
        });

        if (!response.ok) {
          throw new Error('Page request failed');
        }

        const html = await response.text();

        if (
          !this.isConnected ||
          this.request !== request ||
          this.state === 'paused'
        ) {
          return;
        }

        if (this.contextKey() !== this.context) {
          this.pause();
          return;
        }

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const source = parsed.getElementById('ProductGridContainer');

        const incoming =
          source && source.querySelector('mm-collection-scroll');

        const incomingGrid =
          source && source.querySelector('ul#product-grid');

        const incomingPage = Number(incoming && incoming.dataset.page);

        if (
          !incoming ||
          !incomingGrid ||
          incoming.dataset.sectionId !== this.dataset.sectionId ||
          incomingPage !== this.page + 1
        ) {
          throw new Error('Unexpected collection response');
        }

        const existing = new Set(
          Array.from(this.grid.children, (node) => node.dataset.productId)
        );

        const added = Array.from(incomingGrid.children).filter((node) => {
          const id = node.dataset.productId;

          if (!id || existing.has(id)) return false;

          existing.add(id);
          return true;
        });

        if (!added.length) {
          throw new Error('No new products returned');
        }

        const focusedButton = document.activeElement === this.button;
        const fragment = document.createDocumentFragment();

        for (const card of added) {
          card.querySelectorAll('img').forEach((img) => {
            img.loading = 'lazy';
          });

          fragment.appendChild(card);
        }

        this.prepareAnimations(added);
        this.grid.appendChild(fragment);

        this.page = incomingPage;
        this.next = incoming.dataset.nextUrl || '';

        this.dataset.page = String(this.page);
        this.dataset.nextUrl = this.next;

        this.state = this.next ? 'idle' : 'done';
        this.render();

        if (this.state === 'done') {
          this.status.textContent = this.dataset.doneText;

          if (focusedButton) {
            this.status.focus({ preventScroll: true });
          }
        }

        if (
          window.Shopify &&
          window.Shopify.PaymentButton &&
          window.Shopify.PaymentButton.init
        ) {
          try {
            window.Shopify.PaymentButton.init();
          } catch (error) {}
        }

        this.dispatchEvent(
          new CustomEvent('mm:collection:append', {
            bubbles: true,
            detail: {
              page: this.page,
              added: added.length,
              hasNextPage: Boolean(this.next)
            }
          })
        );
      } catch (error) {
        if (
          this.isConnected &&
          this.request === request &&
          this.state !== 'paused'
        ) {
          this.state = 'error';
          this.render();
        }
      } finally {
        if (this.request === request) {
          clearTimeout(this.timeout);

          this.request = null;
          this.grid.removeAttribute('aria-busy');
          this.frame = setTimeout(() => this.arm(), 0);
        }
      }
    }
  }

  customElements.define('mm-collection-scroll', CollectionScroll);
})();